import { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Space,
  Button,
  Alert,
  Table,
  Tag,
} from 'antd';
import { LinkOutlined, ReloadOutlined } from '@ant-design/icons';
import { env } from '@/utils/env';
import { formatCNY } from '@/utils/format';
import { getPlatformSummary, type PlatformSummary } from '@/api/dashboard';

// Dashboard renders coarse platform-level KPIs (in line with the spec's
// /admin/v1/dashboard/platform) and offers a deep-link to Grafana for the
// real-time + retention boards that don't belong inside an admin SPA.
//
// We deliberately keep this lightweight: PMs use it for "is anything obviously
// broken?" not for analytical drill-down — that lives in Grafana / SuperSet.
export function DashboardOverview() {
  const [data, setData] = useState<PlatformSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPlatformSummary();
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            平台总览
          </Typography.Title>
          <Typography.Text type="secondary">
            来自 /admin/v1/dashboard/platform，按 App 维度聚合。
          </Typography.Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            刷新
          </Button>
          <Button type="primary" icon={<LinkOutlined />} href={env.grafanaUrl} target="_blank">
            打开 Grafana 详细看板
          </Button>
        </Space>
      </Space>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      <Row gutter={16}>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic title="在线 App 数" value={data?.active_apps ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic title="累计用户" value={data?.total_users ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="跨 App 用户占比"
              value={((data?.cross_app_user_pct ?? 0) * 100).toFixed(1)}
              suffix="%"
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="跨 App 钻石流转"
              value={((data?.cross_app_diamond_flow_pct ?? 0) * 100).toFixed(1)}
              suffix="%"
            />
          </Card>
        </Col>
      </Row>

      <Card title="各 App 当日表现" style={{ marginTop: 16 }} loading={loading}>
        <Table
          rowKey={(r) => String(r.app_id)}
          dataSource={data?.apps ?? []}
          pagination={false}
          size="small"
          columns={[
            {
              title: 'App',
              dataIndex: 'app_name',
              render: (_, row) => (
                <Space>
                  <Tag color="blue">{row.app_slug}</Tag>
                  <span>{row.app_name || row.app_slug}</span>
                </Space>
              ),
            },
            { title: '日活', dataIndex: 'dau_avg', align: 'right' },
            { title: '新增', dataIndex: 'new_users_total', align: 'right' },
            {
              title: 'GMV',
              dataIndex: 'gmv_cents',
              align: 'right',
              render: (v) => formatCNY(v),
            },
            {
              title: 'ROAS',
              dataIndex: 'roas',
              align: 'right',
              render: (v) => (typeof v === 'number' ? v.toFixed(2) : '-'),
            },
          ]}
        />
      </Card>
    </div>
  );
}
